/**
 * @packageDocumentation
 * Contains the bet area widget that lets players adjust stake values, toggle fast play, and display current wager information.
 */
import * as PIXI from "pixi.js"; 
import { Assets } from 'pixi.js'; 
import { BottomBar } from "./bottomBar";
import {betSizes,currencySymbol} from "./server";
import { applyCent, brightnessFilter,DarknessFilter,disabledfilter, font_main } from "./global";
import { TextureSource } from '@pixi/core';
import { BaseTexture, Texture, Rectangle } from '@pixi/core';
import { SoundManager } from "./sounds";
import { EnumEventBus, EventBus } from "../EventBus";
import addInteraction from "../lib/addButtonInteraction";

/**
 * Interface extending PIXI.Container to include optional background image
 */
interface ContainerWithBg extends PIXI.Container{
    bgImg?:PIXI.AnimatedSprite
}

/**
 * Class representing the betting area UI component
 * @extends PIXI.Container
 */
export class betArea extends PIXI.Container{
    /** Text display for the bet value */
    public betval:PIXI.Text = new PIXI.Text({text:""});
    /** Container for the bet text display */
    public betTextContainer!: ContainerWithBg;
    /** Container for the plus button */
    public plusContainer!:ContainerWithBg
    /** Container for the minus button */
    public minusContainer!:ContainerWithBg
    /** Current index in the bet sizes array */
    static currentBetIndex:number = 0;
    /** Flag indicating if game has started */
    public gameStarted:boolean = false;
    /** Text display for "Play Amount" label */
    private betText:PIXI.Text = new PIXI.Text({text:""});

    /**
     * Creates an instance of betArea
     */
    constructor(){
        super();
        this.CreatePlusText()
        this.CreateMinusText() 
        this.CreateBetTextContainer() 
    }

    /**
     * Creates and configures the bet text display container
     */
    CreateBetTextContainer(){
        this.betTextContainer = new PIXI.Container();

        let bulbFrames:PIXI.Texture[] = [Assets.get("Bet_but"),Assets.get("Bet_but")]
        let BetBg = new PIXI.AnimatedSprite(bulbFrames)
        BetBg.gotoAndStop(0)
        BetBg.anchor.set(0.5);
        BetBg.scale.set(0.75);
        this.betText.text = "PLAY AMOUNT";
        this.betText.style = {
            fontFamily: font_main, 
            align:"center",
            fill:"white",
            fontSize: 23,
            fontWeight: 'lighter',
            dropShadow: {
                alpha: 0.8,
                angle: 1,
                blur: 4,
                color: '0x111111',
                distance: 2,
            },
            stroke:{
                color:"black",
                width:4,
                join:"round"
            },
            letterSpacing:2
        };
        this.betText.anchor.set(0.5);
        this.betText.y = -52+2;

        this.betval.text = currencySymbol+(betSizes[betArea.currentBetIndex].toFixed(2));
        applyCent(this.betval,betSizes[betArea.currentBetIndex]) 

        this.betval.style = {
            fontFamily: font_main, 
            align:"center",
            fill: 'white', 
            fontSize: 30,
            fontWeight: 'bold',
            stroke:{
                color:"black",
                width:4,
                join:"round"
            },
            letterSpacing:1
        };
        this.betval.anchor.set(0.5);
        this.betTextContainer.bgImg = BetBg;
        this.betval.y = 2;

        this.betTextContainer.addChild(BetBg,this.betText,this.betval) 
        this.betval.scale.set(1);
        if(this.betval.width>(this.betTextContainer.bgImg!.width-30)){
            this.betval.scale.set((this.betTextContainer.bgImg!.width-30)/(this.betval.width)); 
        }
        BottomBar.updateTotalBet(betSizes[betArea.currentBetIndex]) 
        this.addChild(this.betTextContainer); 
        this.betTextContainer.interactive = true
    }

    /**
     * Creates and configures the plus button
     */
    CreatePlusText(){
        this.plusContainer = new PIXI.Container();
        let plusBg =  new PIXI.Sprite(Assets.get("Bet_plus_but"));
        plusBg.anchor.set(0.5); 
        plusBg.scale.set(0.75);
        
        let plusText = new PIXI.Text();
        plusText.text = "";
        plusText.style = {
            fontFamily: font_main,
            dropShadow: {
                alpha: 0.8,
                angle: 1,
                blur: 4,
                color: '0x111111',
                distance: 2,
            },
            align:"center",
            fill: 'white', 
            fontSize: 40,
            fontWeight: 'lighter',
        };
        plusText.anchor.set(0.5)
        this.plusContainer.addChild(plusBg,plusText);
        this.plusContainer.x = 100;
        const onHandleTapFunc = () => this.plusClickFun();
        addInteraction(this.plusContainer, onHandleTapFunc, false);

        this.plusContainer.on("pointerup",()=>{
            this.plusContainer.filters = [brightnessFilter] 
        })
        this.plusContainer.on("pointerdown",()=>{
            this.plusContainer.filters = [disabledfilter] 
        })
        this.addChild(this.plusContainer); 
    }

    /**
     * Creates and configures the minus button
     */
    CreateMinusText(){
        this.minusContainer = new PIXI.Container(); 
        let minusBg =  new PIXI.Sprite(Assets.get("Bet_minus_but"));
        minusBg.anchor.set(0.5); 
        minusBg.scale.set(0.75);
        
        let minusText = new PIXI.Text();
        minusText.text = "";
        minusText.style = {
            fontFamily: font_main,
            dropShadow: {
                alpha: 0.8,
                angle: 1,
                blur: 4,
                color: '0x111111',
                distance: 2,
            },
            align:"center",
            fill: 'white', 
            fontSize: 40,
            fontWeight: 'lighter',
        };
        minusText.anchor.set(0.5);
        
        this.minusContainer.addChild(minusBg,minusText);
        this.minusContainer.x = -100;

        const onHandleTapFunc = () => this.minusClickFun();
        addInteraction(this.minusContainer, onHandleTapFunc, false);
        this.addChild(this.minusContainer);
        this.minusContainer.on("pointerup",()=>{
            this.minusContainer.filters = [brightnessFilter] 
        });
        this.minusContainer.on("pointerdown",()=>{
            this.minusContainer.filters = [disabledfilter] 
        });
        this.minusContainer.filters = [disabledfilter];
    }

    /**
     * Handler for plus button click
     * Increases bet amount if game hasn't started
     */
    plusClickFun(){ 
        // if(this.gameStarted==false){
            this.minusContainer.interactive = true;
            this.minusContainer.filters = [] 

            betArea.currentBetIndex++
            if(betArea.currentBetIndex<=betSizes.length-1){
                SoundManager.coinDownSndPlay();
            }
            if(betArea.currentBetIndex>=betSizes.length-1){
                betArea.currentBetIndex = betSizes.length-1
                this.plusContainer.filters = [disabledfilter] 
                this.plusContainer.interactive = false
            }
            this.updateBetText()
            console.log("plus clicked")
        // } 
    }

    /**
     * Handler for minus button click
     * Decreases bet amount if game hasn't started
     */
    minusClickFun(){
    //   if(this.gameStarted==false){
        this.plusContainer.interactive = true 
        this.plusContainer.filters = []
        betArea.currentBetIndex-- 
        if(betArea.currentBetIndex>=0){
            SoundManager.coinUpSndPlay(); 
        }
        if(betArea.currentBetIndex<=0){
            betArea.currentBetIndex = 0
            this.minusContainer.filters = [disabledfilter] 
            this.minusContainer.interactive = false 
        }

        this.updateBetText();
        console.log("minus clicked")
    //   }        
    }

    /**
     * Updates the bet text display and related UI elements
     */
    updateBetText(){
        const bet = betSizes[betArea.currentBetIndex].toFixed(2);
        this.betval.text = currencySymbol+bet;
        EventBus.emit(EnumEventBus.setPlayAmount, bet)
        applyCent(this.betval,betSizes[betArea.currentBetIndex]) 

        BottomBar.updateTotalBet(betSizes[betArea.currentBetIndex]) 
        this.betval.scale.set(1);
        if(this.betval.width>(this.betTextContainer.bgImg!.width-30)){
            this.betval.scale.set((this.betTextContainer.bgImg!.width-30)/(this.betval.width)); 
        }
    }
}
